import {
  DEFAULT_LIST_INITIAL_STATE,
  DEFAULT_LIST_STORAGE,
  LIST_TABLE_APPEARANCE,
} from '@app/common/listTableAppearance';
import TableLink from '@app/components/common/TableLink';
import { listMyPatients } from '@app/features/doctorPortal/api';
import { doctorPatientCardPath } from '@app/features/doctorPortal/doctorLinks';
import type { DoctorPatient } from '@app/features/doctorPortal/types';
import { ClientDisplay } from '@feat-scheduling/components/VisitCalendarEvent/ClientDisplay';
import { APP_PREFIX, DOCTOR_PORTAL_PAGE, DOCTOR_PORTAL_VISITS } from '@app/routes/urls';
import { CalendarOutlined, IdcardOutlined } from '@ant-design/icons';
import { BaseColumn, RowAction, TableRow } from '@konnecta/generic-table';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { LIST_DATE_FORMAT } from '@app/utils/dates/dateFormats';
import { Switch, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/**
 * The doctor's own patients.
 *
 * Same GenericDataTable as every other list in the portal, so column sizing,
 * search, the storage of preferences and the look are the ones the product
 * already has — this is a list of a different thing, not a different kind of
 * list.
 *
 * There is no doctor id anywhere in this page. Scope comes from the token and is
 * enforced by row-level security through `client_doctors`, so the rows are
 * whatever the server decides; nothing here could widen them.
 *
 * A patient whose relationship has ended drops out of this list unless the
 * toggle above it is on, which is why the empty state reads as "none assigned".
 *
 * A ROW IS A CLIENT, NOT A LINK. One patient can hold several links to the same
 * doctor and they close one at a time, so "ended" is `isActive === false` and
 * never `endDate != null`.
 */

interface DoctorPatientRow extends DoctorPatient, TableRow {
  id: string | number;
}

const DATE_UNSET = '—';

// GenericDataTable renders no search box at all unless the external param is
// named here — `searchPlaceholder` alone is not enough — and this is also what
// gets the typed term to `fetchData` as `query`.
const DOCTOR_PATIENT_URL_PARAM_CONFIG = {
  external: {
    searchTerm: 'search',
  },
} as const;

// The backend's cap on `limit`. See fetchPatients: searching means pulling the
// whole list once, because the endpoint has nothing to search with.
const PATIENTS_MAX_PAGE = 1000;

/**
 * True when every word typed appears in the patient's name.
 *
 * Word-wise rather than a single substring so "gregory house" matches whichever
 * order the doctor types the two names in.
 */
const matchesPatient = (patient: DoctorPatient, terms: string[]): boolean => {
  const name = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.toLowerCase();
  return terms.every((term) => name.includes(term));
};

/** What each sortable column sorts ON, since the value shown is not always the value ordered. */
const SORT_VALUES: Record<string, (patient: DoctorPatient) => string | number> = {
  name: (p) => `${p.lastName ?? ''} ${p.firstName ?? ''}`.toLowerCase(),
  doctorType: (p) => p.doctorType ?? '',
  startDate: (p) => p.startDate ?? '',
  // An active patient sorts as empty, not as their end date, for the same
  // reason a never-visited one does: they have no "until". Reading `endDate`
  // here instead would float every blank active row to the top of an ascending
  // sort, which is exactly the rows the column is being used to skip past.
  endDate: (p) => (p.isActive === false ? (p.endDate ?? '') : ''),
  lastVisitAt: (p) => p.lastVisitAt ?? '',
  nextVisitAt: (p) => p.nextVisitAt ?? '',
  completedVisits: (p) => p.completedVisits,
  scheduledVisits: (p) => p.scheduledVisits,
};

/**
 * Order the roster.
 *
 * Done HERE, over the whole list, because `/doctor/patients` takes offset and
 * limit and nothing else — there is no sort parameter, and its SQL is a fixed
 * ORDER BY last_name, first_name. Sorting only the rows already on screen would
 * reorder a page against a list that is still ordered by name underneath, which
 * looks like sorting and is not.
 *
 * `sort` IS A COMMA-SEPARATED LIST, not one field. The table accumulates sorts as
 * columns are clicked and joins them — "-doctorType,scheduledVisits" — so an
 * earlier click stays as the primary key. Reading only the first field is not a
 * simplification: a parser that expects one field finds no column called
 * "doctorType,scheduledVisits", matches nothing, and returns the list untouched.
 * That is silent, and it is what made every sort after the first one do nothing.
 *
 * A row with no value sinks to the bottom whichever way the column is pointed:
 * "never visited" is not a very early date, and floating it to the top of an
 * ascending sort would bury the patients the column is being used to find.
 */
const sortPatients = (patients: DoctorPatient[], sort?: string): DoctorPatient[] => {
  if (!sort) return patients;

  const keys = sort
    .split(',')
    .map((field) => field.trim())
    .filter(Boolean)
    .map((field) => {
      const descending = field.startsWith('-');
      return { read: SORT_VALUES[descending ? field.slice(1) : field], descending };
    })
    .filter((key): key is { read: (p: DoctorPatient) => string | number; descending: boolean } => !!key.read);

  if (keys.length === 0) return patients;

  const isEmpty = (value: string | number) => value === '' || value === null || value === undefined;

  return patients.slice().sort((a, b) => {
    for (const { read, descending } of keys) {
      const left = read(a);
      const right = read(b);
      if (isEmpty(left) !== isEmpty(right)) return isEmpty(left) ? 1 : -1;
      if (left === right) continue;
      return (left < right ? -1 : 1) * (descending ? -1 : 1);
    }
    return 0;
  });
};

/**
 * Grey an ended patient's cell.
 *
 * `isActive === false`, never `endDate != null`: a client can hold one closed
 * and one open link to the same doctor, and that client is a current patient
 * who happens to carry an old end date.
 */
const dimIfEnded = (row: DoctorPatientRow | undefined, content: ReactNode): ReactNode =>
  row?.isActive === false ? <span className="text-slate-400">{content}</span> : content;

const DOCTOR_VISITS_PATH = `/${APP_PREFIX}/${DOCTOR_PORTAL_PAGE}/${DOCTOR_PORTAL_VISITS}`;

// `client_id` is declared with the `==` operator in visitsFilters.config, which
// the table's generator writes to the URL as `eq`.
const CLIENT_ID_FILTER_PARAM = 'filter[client_id][eq]';

const DoctorPatientsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // LIST_DATE_FORMAT, like every other date in the dashboard. This was a
  // 2-digit toLocaleDateString ("03/05/2026"), the one place in the portal that
  // formatted its own dates, and 03/05 is a different day depending on which
  // side of the Atlantic you read it from. dayjs's locale is kept in step with
  // the UI language by App.tsx, so the weekday and month names localise;
  // `i18n.language` stays in the deps to re-render the columns when it changes.
  const formatDate = useCallback(
    (value?: string | null) => (value ? dayjs(value).format(LIST_DATE_FORMAT) : DATE_UNSET),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  // Off by default, so the list opens on the doctor's current roster — the
  // behaviour the page has today. On, it widens to patients every one of whose
  // links to this doctor is closed; they arrive as extra rows, not as a
  // different list.
  const [showEnded, setShowEnded] = useState(false);

  // The flag is only ever sent when it is on. `includeEnded: false` and no
  // `includeEnded` mean the same thing to the endpoint, and sending the one
  // makes the default request look like a filtered one.
  const listParams = useCallback(
    (limit: number, offset: number) => ({ limit, offset, ...(showEnded ? { includeEnded: true } : {}) }),
    [showEnded]
  );

  const fetchPatients = useCallback(
    async (params: { limit: number; offset: number; query?: string; sort?: string }) => {
      const terms = (params.query ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean);

      // Straight through while nothing is being searched or sorted: the endpoint
      // already returns the roster ordered by name, a page at a time.
      if (terms.length === 0 && !params.sort) {
        const response = await listMyPatients(listParams(params.limit, params.offset));
        return { data: response.data, total: response.total };
      }

      // `/doctor/patients` takes offset and limit and NOTHING else — no search
      // term, no sort. Both are therefore done here, over the whole list, which
      // is affordable because this is the handful of people one doctor actually
      // attends rather than a directory. The alternative for sorting is worse
      // than not offering it: reordering one page of a list the server still
      // orders by name looks like sorting and is not.
      const response = await listMyPatients(listParams(PATIENTS_MAX_PAGE, 0));
      const matches = terms.length ? response.data.filter((patient) => matchesPatient(patient, terms)) : response.data;
      const ordered = sortPatients(matches, params.sort);

      return { data: ordered.slice(params.offset, params.offset + params.limit), total: ordered.length };
    },
    [listParams]
  );

  const transformToRow = (patient: DoctorPatient): DoctorPatientRow => ({ ...patient, id: patient.clientId });

  // The doctor has no client record page, so a patient leads to their visits.
  // The destination reads `filter[...]` straight off the URL, so the filter is
  // carried there as a query param rather than as state.
  // The record is the primary destination now: one patient in full rather than a
  // filtered list of their visits. The visits link stays as a second action,
  // because "show me everything that happened" is a different question.
  const openPatientCard = useCallback(
    (clientId: string | number) => navigate(doctorPatientCardPath(clientId)),
    [navigate]
  );

  const openPatientVisits = useCallback(
    (clientId: string | number) => {
      const params = new URLSearchParams({ [CLIENT_ID_FILTER_PARAM]: String(clientId), page: '1' });
      navigate(`${DOCTOR_VISITS_PATH}?${params.toString()}`);
    },
    [navigate]
  );

  const columns: BaseColumn<DoctorPatientRow>[] = useMemo(
    () => [
      {
        id: 'name',
        sortable: true,
        sortField: 'name',
        header: t('doctor-patients-col-name'),
        accessor: (row) => `${row.lastName} ${row.firstName}`,
        width: 220,
        // The same avatar-and-name the visits list and the calendar use for a
        // client, so one person looks like one person wherever they appear — and
        // the same link on it, so a name is clickable in both lists rather than
        // only where a row action happens to be looked for.
        cellRenderer: (_, row) =>
          row ? (
            <div className="flex min-w-0 items-center gap-2">
              {dimIfEnded(
                row,
                <TableLink to={doctorPatientCardPath(row.clientId)} onClick={() => openPatientCard(row.clientId)}>
                  <ClientDisplay firstName={row.firstName ?? ''} lastName={row.lastName ?? ''} />
                </TableLink>
              )}
              {/* The state said in words on the row itself. Grey alone is a
                  hint, and a reader who has not noticed the Until column is
                  looking at a patient they think is current. */}
              {row.isActive === false ? <Tag className="!m-0">{t('doctor-patients-ended-tag')}</Tag> : null}
            </div>
          ) : (
            DATE_UNSET
          ),
      },
      {
        id: 'startDate',
        sortable: true,
        sortField: 'startDate',
        header: t('doctor-patients-col-since'),
        accessor: (row) => row.startDate ?? '',
        width: 170,
        // MIN(client_doctors.start_date): when this doctor took the patient on,
        // not when the patient joined the service. A doctor holding both an
        // attending and a referring link sees the earlier of the two.
        cellRenderer: (_, row) => dimIfEnded(row, formatDate(row?.startDate)),
      },
      {
        id: 'endDate',
        sortable: true,
        sortField: 'endDate',
        header: t('doctor-patients-col-until'),
        accessor: (row) => (row.isActive === false ? (row.endDate ?? '') : ''),
        width: 170,
        // Blank for anyone still a patient. A client holding a closed REFERRING
        // link and an open ATTENDING one carries the closed one's end date, and
        // printing it would date the end of a relationship that is running.
        cellRenderer: (_, row) => dimIfEnded(row, row?.isActive === false ? formatDate(row.endDate) : DATE_UNSET),
      },
      {
        id: 'doctorType',
        sortable: true,
        sortField: 'doctorType',
        header: t('doctor-patients-col-link'),
        accessor: (row) => row.doctorType,
        width: 180,
        // A doctor may hold both an attending and a referring link to the same
        // patient; the API sends them comma-separated rather than duplicating
        // the row, so one patient is one row here too.
        cellRenderer: (_, row) =>
          dimIfEnded(
            row,
            row?.doctorType
              ? row.doctorType.split(',').map((type) => (
                  <Tag key={type} className="!m-0 !mr-1">
                    {t(`doctor-link-type-${type.toLowerCase()}`, type)}
                  </Tag>
                ))
              : DATE_UNSET
          ),
      },
      {
        id: 'lastVisitAt',
        sortable: true,
        sortField: 'lastVisitAt',
        header: t('doctor-patients-col-last-visit'),
        accessor: (row) => row.lastVisitAt ?? '',
        width: 170,
        cellRenderer: (_, row) => dimIfEnded(row, formatDate(row?.lastVisitAt)),
      },
      {
        id: 'nextVisitAt',
        sortable: true,
        sortField: 'nextVisitAt',
        header: t('doctor-patients-col-next-visit'),
        accessor: (row) => row.nextVisitAt ?? '',
        width: 170,
        cellRenderer: (_, row) => dimIfEnded(row, formatDate(row?.nextVisitAt)),
      },
      {
        id: 'completedVisits',
        sortable: true,
        sortField: 'completedVisits',
        header: t('doctor-patients-col-completed'),
        accessor: (row) => row.completedVisits,
        width: 130,
        // The counts are greyed with the rest of the row: half a row in grey
        // reads as a rendering fault rather than as a state.
        cellRenderer: (_, row) => dimIfEnded(row, row?.completedVisits),
      },
      {
        id: 'scheduledVisits',
        sortable: true,
        sortField: 'scheduledVisits',
        header: t('doctor-patients-col-scheduled'),
        accessor: (row) => row.scheduledVisits,
        width: 140,
        cellRenderer: (_, row) => dimIfEnded(row, row?.scheduledVisits),
      },
    ],
    [t, formatDate, openPatientCard]
  );

  const tableConfig = useMemo(
    () => ({
      tableId: 'doctor-patients',
      initialState: DEFAULT_LIST_INITIAL_STATE,
      storage: DEFAULT_LIST_STORAGE,
      urlParamConfig: DOCTOR_PATIENT_URL_PARAM_CONFIG,
      fetchData: fetchPatients,
      transformData: transformToRow,
      rowKey: 'id' as const,
      columns,
      searchPlaceholder: t('doctor-patients-search-placeholder'),
      onRowDoubleClick: (row: DoctorPatientRow) => openPatientCard(row.clientId),
      actions: {
        getActions: (row: DoctorPatientRow): RowAction<DoctorPatientRow>[] => [
          {
            key: 'card',
            label: <Tooltip title={t('doctor-patients-open-card-tooltip')}>{t('doctor-patients-open-card')}</Tooltip>,
            icon: <IdcardOutlined />,
            onClick: () => openPatientCard(row.clientId),
          },
          {
            key: 'visits',
            label: (
              <Tooltip title={t('doctor-patients-open-visits-tooltip')}>{t('doctor-patients-open-visits')}</Tooltip>
            ),
            icon: <CalendarOutlined />,
            onClick: () => openPatientVisits(row.clientId),
          },
        ],
        column: { header: t('actions'), sticky: 'right' as const, align: 'center' as const, width: 56 },
      },
    }),
    [columns, fetchPatients, openPatientCard, openPatientVisits, t]
  );

  // min-h-0 so the table scrolls inside the flex column instead of pushing the page.
  return (
    <div className="flex-1 min-h-0">
      <AppDataTable
        appearance={LIST_TABLE_APPEARANCE}
        config={tableConfig}
        // `leftContent` is the toolbar slot beside the search box, so the
        // toggle sits with the other controls rather than in a band above them.
        leftContent={
          <div className="flex items-center gap-2">
            <Switch size="small" checked={showEnded} onChange={setShowEnded} />
            <span className="text-xs">{t('doctor-patients-show-ended')}</span>
          </div>
        }
        // A refresh token, not a `key`: remounting would throw away the search
        // term and the page the doctor is on. It names the snapshot the rows
        // came from, because that is what the table diffs to decide it must
        // fetch again — a constant here leaves a widened list unfetched.
        refreshToken={showEnded ? 'with-ended' : 'active-only'}
      />
    </div>
  );
};

export default DoctorPatientsPage;
